import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { collection, getDocs, setDoc, doc } from "firebase/firestore";
import { db } from "../../firebase";
import {
  FaVideo,
  FaCode,
  FaFont,
  FaFileAlt,
  FaImage,
  FaLink,
  FaUpload,
  FaRegFilePdf,
} from "react-icons/fa";
import Notification from "../../component/notification";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import TextareaAutosize from '@mui/material/TextareaAutosize';

// Helper function to find the actual day document ID
const findDayDocumentId = async (courseId, targetDayNumber) => {
  try {
    const dayCollectionRef = collection(db, "courses", courseId, "day");
    const daySnapshot = await getDocs(dayCollectionRef);
    
    // Look for document IDs that match the pattern (day1, day2, etc. or just 1, 2, etc.)
    const dayDocs = daySnapshot.docs.map(doc => ({
      id: doc.id,
      numericValue: extractNumericValue(doc.id)
    }));
    
    // Find the document that matches our target day number
    const matchingDoc = dayDocs.find(doc => doc.numericValue === targetDayNumber);
    return matchingDoc ? matchingDoc.id : `${targetDayNumber}`; // Fallback to numeric string
  } catch (error) {
    console.error("Error finding day document ID:", error);
    return `day${targetDayNumber}`; // Fallback to day prefix format
  }
};

// Helper function to extract numeric value from document ID
const extractNumericValue = (docId) => {
  // Handle formats like "day1", "day2" or just "1", "2"
  const match = docId.match(/\d+/);
  return match ? parseInt(match[0], 10) : null;
};

async function addAssignment(courseId, dayDocId, assignmentData, type) {
  // Use the actual day document ID instead of constructing it
  const assignmentsRef = collection(
    db,
    "courses",
    courseId,
    "day",
    dayDocId, // Use actual document ID found in database
    "assignments"
  );
  const snapshot = await getDocs(assignmentsRef);
  let maxNum = 0;
  snapshot.docs.forEach((docSnap) => {
    const match = docSnap.id.match(/assignment (\d+)/);
    if (match) {
      const num = parseInt(match[1], 10);
      if (num > maxNum) maxNum = num;
    }
  });
  const newId = `assignment ${maxNum + 1}`;
  // Use the actual day document ID
  const assignmentDocRef = doc(
    db,
    "courses",
    courseId,
    "day",
    dayDocId, // Use actual document ID found in database
    "assignments",
    newId
  );
  await setDoc(assignmentDocRef, {
    ...assignmentData,
    type,
    createdDate: new Date().toISOString(),
  });
}

const assignmentTypes = [
  {
    value: "video",
    label: "Video",
    icon: <FaVideo size={28} color="#7c3aed" />,
  },
  {
    value: "program",
    label: "Program",
    icon: <FaCode size={28} color="#7c3aed" />,
  },
  { value: "text", label: "Text", icon: <FaFont size={28} color="#7c3aed" /> },
];

const StudentContentForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const course = location.state?.course;
  const day = location.state?.day; // Numeric day value (1, 2, 3, ...)
  const dayDocId = location.state?.dayDocId; // Actual document ID from navigation

  const [assignmentType, setAssignmentType] = useState("");
  const [actualDayDocId, setActualDayDocId] = useState(dayDocId || null); // Store the actual document ID
  const [form, setForm] = useState({
    title: "",
    description: "",
    thumbnail: "",
    documentUrl: "",
    file: null,
    videoLink: "",
  });
  const [notification, setNotification] = useState({
    visible: false,
    message: "",
    type: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Initialize the actual day document ID if not provided
  useEffect(() => {
    const initializeDayDocId = async () => {
      if (!actualDayDocId && course?.id && day) {
        try {
          const foundDocId = await findDayDocumentId(course.id, day);
          setActualDayDocId(foundDocId);
        } catch (error) {
          console.error("Error initializing day document ID:", error);
          setNotification({
            visible: true,
            message: "Error finding day document. Please try again.",
            type: "failure",
          });
        }
      }
    };

    initializeDayDocId();
  }, [course, day, actualDayDocId]);

  const handleTypeSelect = (type) => {
    setAssignmentType(type);
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "file") {
      setForm((prev) => ({ ...prev, file: files[0] }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!actualDayDocId) {
      setNotification({
        visible: true,
        message: "❗ Day document ID not found. Please try again.",
        type: "failure",
      });
      return;
    }

    let errs = {};
    if (!form.title) errs.title = "Required";
    if (!form.description) errs.description = "Required";
    if (!form.thumbnail) errs.thumbnail = "Required";
    if (!form.documentUrl) errs.documentUrl = "Required";
    if (!form.videoLink) errs.videoLink = "Required";

    setErrors(errs);
    if (Object.keys(errs).length === 0) {
      setLoading(true);
      setNotification({
        visible: true,
        message: "Submitting...",
        type: "loading",
      });
      try {
        
       
        if (!form.file) {
          throw new Error("No file selected");
        }
        const storage = getStorage();
       
        const storagePath = `courses/${course.id}/day/${actualDayDocId}/assignments/${form.file.name}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, form.file);
        const fileURL = await getDownloadURL(storageRef);
        

        const assignmentData = {
          title: form.title,
          description: form.description,
          thumbnail: form.thumbnail,
          documentUrl: form.documentUrl,
          videoLink: form.videoLink,
          fileURL: fileURL, 
          storagePath: storagePath, 
          createdDate: new Date().toISOString(),
        };

        await addAssignment(course.id, actualDayDocId, assignmentData, assignmentType);
        setNotification({
          visible: true,
          message: "✅ Content added successfully!",
          type: "success",
        });
        setLoading(false);
        
        navigate("/coursecontent", {
          state: { 
            course, 
            day, 
            dayDocId: actualDayDocId, 
            contentAdded: true 
          },
        });
      } catch (error) {
        console.error("Content submission error:", error);
        setNotification({
          visible: true,
          message: `❗ Failed to add content: ${error.message}`,
          type: "failure",
        });
        setLoading(false);
      }
    } else {
      setNotification({
        visible: true,
        message: "⚠️ Please fill in all required fields.",
        type: "warning",
      });
    }
    setTimeout(
      () => setNotification({ visible: false, message: "", type: "" }),
      3000
    );
  };

  return (
    <div
      className="border rounded shadow-sm p-4 bg-white"
      style={{ maxWidth: 800, margin: "2rem auto" }}
    >
      <Notification
        message={notification.message}
        type={notification.type}
        visible={notification.visible}
        onClose={() =>
          setNotification({ visible: false, message: "", type: "" })
        }
      />
      <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
        <h2
          style={{
            fontWeight: 700,
            fontSize: "2rem",
            letterSpacing: "0.5px",
            margin: 0,
          }}
        >
          {course?.title} - Day {day}
          {actualDayDocId && actualDayDocId !== `${day}` && (
            <small className="text-muted ms-2">({actualDayDocId})</small>
          )}
        </h2>
        <div className="mb-2 text-muted" style={{ fontSize: "1rem" }}>
          Fill in the details to add new content.
        </div>
      </div>
      {!assignmentType ? (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 4, mt: 6 }}>
          {assignmentTypes.map((type) => (
            <Button
              key={type.value}
              variant="outlined"
              onClick={() => handleTypeSelect(type.value)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 3,
                minWidth: 120,
                borderColor: "#a78bfa",
                color: "#7c3aed",
                fontWeight: 600,
                fontSize: "1.1rem",
                background: "#ede9fe",
                borderRadius: 3,
                boxShadow: "0 2px 8px #a78bfa22",
                "&:hover": { background: "#ddd6fe", borderColor: "#7c3aed" },
              }}
            >
              {type.icon}
              <span style={{ marginTop: 8 }}>{type.label}</span>
            </Button>
          ))}
        </Box>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="container"
          style={{ maxWidth: 500 }}
        >
          <Box
            sx={{
              mb: 2,
              color: "#6d28d9",
              fontSize: "1.1rem",
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <FaFileAlt style={{ marginRight: 8, color: "#a78bfa" }} /> Fill in
            the details for the assignment.
          </Box>
          <TextField
            name="title"
            label={
              <span>
                <FaFileAlt style={{ color: "#a78bfa", marginRight: 6 }} /> Title
              </span>
            }
            value={form.title}
            onChange={handleChange}
            fullWidth
            sx={{ mb: 3, borderRadius: 2, width: "100%" }}
            error={!!errors.title}
            helperText={errors.title}
          />
          <TextField
            name="thumbnail"
            label={
              <span>
                <FaImage style={{ color: "#a78bfa", marginRight: 6 }} />{" "}
                Thumbnail URL
              </span>
            }
            value={form.thumbnail}
            onChange={handleChange}
            fullWidth
            sx={{ mb: 3, borderRadius: 2 }}
            error={!!errors.thumbnail}
            helperText={errors.thumbnail}
          />
          <TextField
            name="documentUrl"
            label={
              <span>
                <FaRegFilePdf style={{ color: "#a78bfa", marginRight: 6 }} />{" "}
                Document URL
              </span>
            }
            value={form.documentUrl}
            onChange={handleChange}
            fullWidth
            sx={{ mb: 3, borderRadius: 2 }}
            error={!!errors.documentUrl}
            helperText={errors.documentUrl}
          />
          
          <TextField
            type="file"
            name="file"
            onChange={handleChange}
            fullWidth
            sx={{ mb: 3, borderRadius: 2 }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ style: { padding: 8, borderRadius: 8, paddingTop: 20, paddingBottom: 20 } }}
            label={<span><FaUpload style={{ color: "#a78bfa", marginRight: 6 }} /> File Upload</span>}
            error={!!errors.file}
            helperText={errors.file}
          />
         
          <TextField
            name="videoLink"
            label={
              <span>
                <FaLink style={{ color: "#a78bfa", marginRight: 6 }} /> Video
                Link
              </span>
            }
            value={form.videoLink}
            onChange={handleChange}
            fullWidth
            sx={{ mb: 3, borderRadius: 2 }}
            error={!!errors.videoLink}
            helperText={errors.videoLink}
          />
          <TextareaAutosize
            name="description"
            minRows={3}
            maxRows={10}
            value={form.description}
            onChange={handleChange}
            style={{ width: '100%', borderRadius: 8, padding: 8, fontSize: 16 }}
            placeholder="Description"
          />
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "1rem",
            }}
          >
            <Button
              onClick={() => setAssignmentType("")}
              variant="outlined"
              color="inherit"
              sx={{
                borderRadius: 2,
                px: 4,
                fontWeight: 500,
                borderColor: "#a78bfa",
                color: "#7c3aed",
                "&:hover": { borderColor: "#7c3aed", background: "#ede9fe" },
              }}
            >
              Back
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !actualDayDocId}
              sx={{
                borderRadius: 2,
                px: 4,
                fontWeight: 500,
                ml: 2,
                background: "#a78bfa",
                "&:hover": { background: "#7c3aed" },
                "&:disabled": { background: "#e5e7eb" },
              }}
            >
              {loading ? "Adding..." : "Add Content"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default StudentContentForm;